import {
  BROADCAST_TYPE,
  KAKAO_SHARE_TEMPLATE_ID_POST,
  KAKAO_SHARE_TEMPLATE_ID_VOD,
  UCC_FILE_TYPE,
} from '@/constants/channel';
import {PostModel} from '@/types/channel/board/board.model';
import globalDomains from '../global-domains';
import {PostListUccFileType} from '@/types/channel/board/board.schema';
import {scheme} from '@afreecatv/domains';
import {isIOS} from 'react-device-detect';
import {goLinkWithChannelBridge} from '@/components/channel/common/Bridge';
import {ChannelT} from '../i18n/channel';

interface GenerateSharePopupParams {
  post: PostModel;
  stationUserId: string;
}

interface GenerateSharePopupReturn {
  title: string;
  pageUrl: string;
  kakaoParams: {
    templateId: number;
    templateArgs: Record<string, any>;
  };
}

export const getPostUrl = (
  post: Pick<PostModel, 'id' | 'broadcastType' | 'ucc'>,
  stationUserId: string,
) => {
  if (post.broadcastType === BROADCAST_TYPE.UCC) {
    if (post.ucc?.fileType === UCC_FILE_TYPE.CATCH) {
      return `${globalDomains.VOD_AFREECATV}/${post.id}/catch`;
    }
    return `${globalDomains.VOD_AFREECATV}/player/${post.id}`;
  } else if (post.ucc && post.ucc.fileType === UCC_FILE_TYPE.CATCHSTORY) {
    return `${globalDomains.VOD_AFREECATV}/${post.ucc?.storyId}/catchstory?o=1`;
  } else {
    return `${globalDomains.WWW_AFREECATV}/station/${stationUserId}/post/${post.id}`;
  }
};

export const createPostShareParams = ({
  post,
  stationUserId,
}: GenerateSharePopupParams): GenerateSharePopupReturn => {
  const shareUrl = getPostUrl(post, stationUserId);

  const isUCC = post.broadcastType === BROADCAST_TYPE.UCC;
  const isCatchStory = post.ucc?.fileType === UCC_FILE_TYPE.CATCHSTORY;

  const title = (() => {
    if (typeof post.title === 'string') {
      return post.title.replace(/(<([^>]+)>)/gi, '').replace(/"/g, "'");
    }
    if (isCatchStory && post.date) {
      const [_, month, day] = post.date.split('-');
      return ChannelT(`{{month}}월 {{day}}일 스토리`, {
        placeholder: {month: Number(month), day: Number(day)},
      });
    }
    return '';
  })();

  const imageUrl =
    isUCC || isCatchStory
      ? (post.ucc?.thumbnailUrl ?? '')
      : (post.thumbnailUrl ?? '');

  const templateId =
    isUCC || isCatchStory
      ? KAKAO_SHARE_TEMPLATE_ID_VOD
      : KAKAO_SHARE_TEMPLATE_ID_POST;

  const videoNumber = isUCC
    ? post.id
    : isCatchStory
      ? shareUrl.split(globalDomains.VOD_AFREECATV + '/')[1]
      : `post/${post.id}`;

  const scheme = isUCC
    ? `${process.env.SOOP_APP_SCHEME}player/video?station_no=${post.stationId}&bbs_no=${post.bbsId}&title_no=${post.id}`
    : isCatchStory
      ? `${process.env.SOOP_APP_SCHEME}player/catchstory?nStoryIdx=${post.ucc?.storyId}`
      : `${process.env.SOOP_APP_SCHEME}browser/station?url=${shareUrl}&title=${encodeURIComponent(title)}`;

  return {
    title,
    pageUrl: shareUrl,
    kakaoParams: {
      templateId,
      templateArgs: {
        bjId: stationUserId,
        title,
        videoNumber,
        description: post.content.replace(/(<([^>]+)>)/gi, '') ?? '',
        count: post.viewCount ?? 0,
        imageUrl: `https:${imageUrl}`,
        scheme: encodeURIComponent(scheme),
      },
    },
  };
};

export const getVodEditUrl = (
  isMobile: boolean,
  post: Pick<PostModel, 'stationId' | 'bbsId' | 'id'>,
) => {
  if (isMobile) {
    const url = `${globalDomains.M_AFREECATV}/vod/edit/a/index?szWork=edit_title&szLocation=station&nStationNo=${post.stationId}&nBbsNo=${post.bbsId}&nTitleNo=${post.id}`;
    return isIOS ? goLinkWithChannelBridge(url) : url;
  } else {
    return `${globalDomains.STBBS_AFREECATV}/vodupload/index.php?szWork=edit_title&szLocation=station&nStationNo=${post.stationId}&nBbsNo=${post.bbsId}&nTitleNo=${post.id}`;
  }
};

export const getSetMidrollUrl = (
  post: Pick<PostModel, 'stationId' | 'bbsId' | 'id'>,
) => {
  return `${globalDomains.STBBS_AFREECATV}/vodupload/index.php?szWork=midroll&nStationNo=${post.stationId}&nBbsNo=${post.bbsId}&nTitleNo=${post.id}&szNewWindow=on`;
};

export const getSetChapterUrl = (
  post: Pick<PostModel, 'stationId' | 'bbsId' | 'id'>,
) => {
  return `${globalDomains.STBBS_AFREECATV}/vodupload/index.php?szWork=chapter&nStationNo=${post.stationId}&nBbsNo=${post.bbsId}&nTitleNo=${post.id}&szNewWindow=on`;
};

export const getPlaylistUpdateUrl = (playlistId: number) => {
  return `${globalDomains.STBBS_AFREECATV}/vodplaylist/index.php?nPlaylistIdx=${playlistId}`;
};

export const getPlaylistShareUrl = (broadcastId: number, playlistId: number) =>
  `${globalDomains.VOD_AFREECATV}/player/${broadcastId}/playlist/${playlistId}`;

/**
 * 재생목록 아이템에 대해 공유하기 데이터 생성
 */
export const createPlaylistShareParams = ({
  broadcastId,
  playlistId,
  title,
  stationUserId,
  thumbnailUrl,
  stationNo,
  fileType,
  bbsId,
}: {
  broadcastId: number;
  playlistId: number;
  title: string;
  stationUserId: string;
  thumbnailUrl: string;
  stationNo?: number;
  bbsId: number;
  fileType?: PostListUccFileType;
}): GenerateSharePopupReturn => {
  const shareUrl = getPlaylistShareUrl(broadcastId, playlistId);

  const shareTitle = title.replace(/(<([^>]+)>)/gi, '').replace(/"/g, "'");

  return {
    title: shareTitle,
    pageUrl: shareUrl,
    kakaoParams: {
      templateId: KAKAO_SHARE_TEMPLATE_ID_VOD,
      templateArgs: {
        bjId: stationUserId,
        title: shareTitle,
        videoNumber: broadcastId,
        imageUrl: `https:${thumbnailUrl}`,
        scheme: encodeURIComponent(
          `${process.env.SOOP_APP_SCHEME}player/video?station_no=${stationNo}&bbs_no=${bbsId}&title_no=${broadcastId}&nPlaylistIdx=${playlistId}&type=${fileType}`,
        ),
      },
    },
  };
};

export const getVodShareUrl = (
  broadcastType: PostListUccFileType,
  broadcastId: number,
  storyIdx?: number,
) => {
  let shareUrl = `${globalDomains.VOD_AFREECATV}/player/${broadcastId}`;
  if (broadcastType === 'CATCH') {
    shareUrl += '/catch';
  } else if (broadcastType === 'CATCHSTORY') {
    shareUrl = `${globalDomains.VOD_AFREECATV}/player/${storyIdx}/catchstory?o=1`;
  }

  return shareUrl;
};

/**
 * VOD 아이템에 대해 공유하기 데이터 생성
 */
export const createVodShareParams = ({
  title,
  broadcastId,
  broadcastType,
  stationUserId,
  stationNo,
  thumbnailUrl,
  readCount,
  storyIdx,
}: {
  title: string;
  broadcastId: number;
  broadcastType: PostListUccFileType;
  stationUserId: string;
  stationNo: number;
  thumbnailUrl: string;
  readCount: number;
  storyIdx?: number;
}): GenerateSharePopupReturn => {
  let shareUrl = getVodShareUrl(broadcastType, broadcastId, storyIdx);
  const shareTitle = title.replace(/(<([^>]+)>)/gi, '').replace(/"/g, "'");

  return {
    title: shareTitle,
    pageUrl: shareUrl,
    kakaoParams: {
      templateId: KAKAO_SHARE_TEMPLATE_ID_VOD,
      templateArgs: {
        bjId: stationUserId,
        title: shareTitle,
        videoNumber: broadcastId,
        count: readCount,
        imageUrl: `https:${thumbnailUrl}`,
        scheme:
          broadcastType === 'CATCHSTORY'
            ? encodeURIComponent(
                `${process.env.SOOP_APP_SCHEME}player/catchstory?nStoryIdx=${storyIdx}`,
              )
            : encodeURIComponent(
                `${process.env.SOOP_APP_SCHEME}player/video?station_no=${stationNo}&title_no=${broadcastId}`,
              ),
      },
    },
  };
};

/**
 * 방송국에 대해 공유하기 데이터 생성
 */
export const createStationShareParams = ({
  userId,
  userNick,
  stationInfoText,
}): GenerateSharePopupReturn => {
  const shareUrl = `${globalDomains.SOOPLIVE_ROOT_DOMAIN}/station/${userId}`;
  const shareTitle = ChannelT(`{{userNick}}님의 방송국`, {
    placeholder: {userNick: userNick},
  });
  const description =
    stationInfoText === ''
      ? ChannelT(`안녕하세요. {{userNick}}님의 방송국입니다.`, {
          placeholder: {userNick: userNick},
        })
      : stationInfoText;

  return {
    title: shareTitle,
    pageUrl: shareUrl,
    kakaoParams: {
      templateId: KAKAO_SHARE_TEMPLATE_ID_POST,
      templateArgs: {
        bjId: userId,
        description: description,
        title: shareTitle,
        scheme: encodeURIComponent(
          `${scheme.SERVICE_APP_SCHEME}browser/station?url=${shareUrl}`,
        ),
      },
    },
  };
};
